import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UserSessionService, IUser } from '@nha/auth';
import { SessionKeys } from '@nha/auth/enums/session-keys.enums';
import { take } from 'rxjs';
import { NHASnackbarService } from '../snack-bar/snack-bar.service';
import * as Cookies from 'es-cookie';
import { getAppDomain } from '@nha/functions/get-app-domain.function';
import { appSettings } from '@nha/config/app-settings';
import { Environments } from '@nha/enums';
import { AccountService } from '@nha/api/account/services/account.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { IfHasEntitlementDirective } from '../../auth/directives/if-has-entitlement.directive';

import { NHATestDirective } from '../../directives/test/test.directive';
import { NHAAvatarComponent } from '../avatar/avatar.component';
import { NHAPopupComponent } from '../popup/popup.component';

@Component({
	selector: 'nha-user-profile-menu',
	templateUrl: './user-profile-menu.component.html',
	styleUrls: ['./user-profile-menu.component.scss'],
	encapsulation: ViewEncapsulation.None,
	imports: [
		NHAPopupComponent,
		NHAAvatarComponent,
		NHATestDirective,
		IfHasEntitlementDirective,
		FormsModule,
		ReactiveFormsModule,
		MatButtonModule,
		MatTooltipModule,
		MatIconModule
	]
})
export class NHAUserProfileMenuComponent implements OnInit {
	// private readonly _userHubService = inject(UserHubService);
	private readonly _userSessionService = inject(UserSessionService);
	private readonly _accountService = inject(AccountService);
	private readonly _snackBarService = inject(NHASnackbarService);

	private _impersonationCheckTimer: number = null;

	fullName: string = '';
	jobTitle: string = '';
	userGuid: string;
	isImpersonating: boolean = false;
	impersonationUser: string = null;
	canImpersonate: boolean = false;

	userSearch: UntypedFormControl = new UntypedFormControl();

	ngOnInit(): void {
		const user: IUser = this._userSessionService.getUser();
		this.fullName = user.fullName;
		this.jobTitle = user.jobTitle;
		this.userGuid = user.userGuid;
		this.isImpersonating = Cookies.get(SessionKeys.IMPERSONATION_COOKIE) ? true : false;
		this.impersonationUser = Cookies.get(SessionKeys.IMPERSONATION_COOKIE);
		this.canImpersonate = [Environments.DEV, Environments.CI, Environments.QA].includes(appSettings.env);

		this.watchForImpersonationStartEnd();
	}

	signOut(): void {
		this._userSessionService.logout();
	}

	refreshToken(): void {
		this._userSessionService
			.refreshToken(true)
			.then((jwt) => {
				console.log(`Token Refreshed From User Menu: ${jwt}`);
			})
			.catch((error) => console.error('Token refresh from the user menu failed.', error));
	}

	killOktaSession(): void {
		this._userSessionService.killOktaSession().then((_) => {
			console.log(`Okta Session Killed From User Menu`);
		});
	}

	impersonateUser(): void {
		const username: string = this.userSearch.value;

		if (!username) {
			this._snackBarService.error('Please enter a username to impersonate');
			return;
		}

		if (username !== null && username === this._userSessionService.getUser().username) {
			this._snackBarService.error('Why would you want to impersonate yourself...? Come on man... ');
			return;
		}

		if (username !== null) {
			this._snackBarService.info(`Searching for user ${username}...`);

			this._accountService
				.getByUsername(username)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.clearNonEnvCookies();

						Cookies.set(SessionKeys.IMPERSONATION_COOKIE, username, { path: '/', secure: true, domain: getAppDomain() });
						this._snackBarService.success(`About to start impersonation session as ${username}...`);

						setTimeout(() => {
							window.location.reload();
						}, 1000);
					},
					error: () => {
						this._snackBarService.error('Username not found');
					}
				});
		}
	}

	endImpersonationSession(): void {
		this.clearNonEnvCookies();

		Cookies.remove(SessionKeys.IMPERSONATION_COOKIE, { path: '/', secure: true, domain: getAppDomain() });
		this._snackBarService.success(`Ending impersonation session for ${this.impersonationUser}...`);

		setTimeout(() => {
			window.location.reload();
		}, 1000);
	}

	private clearNonEnvCookies(): void {
		for (const cookie in Cookies.getAll()) {
			if (!cookie.startsWith(appSettings.env)) {
				Cookies.remove(cookie, { path: '/', domain: getAppDomain() });
			}
		}
	}

	private watchForImpersonationStartEnd(): void {
		if (!this.canImpersonate) {
			return;
		}

		window.clearInterval(this._impersonationCheckTimer);
		this._impersonationCheckTimer = window.setInterval(() => {
			const isImpersonatingNow: boolean = Cookies.get(SessionKeys.IMPERSONATION_COOKIE) ? true : false;

			if ((this.isImpersonating && !isImpersonatingNow) || (!this.isImpersonating && isImpersonatingNow)) {
				window.location.reload();
			}
		}, 1000);
	}
}
